import {
  useCMSBlogs,
  useCMSFAQ,
  useCMSRelease,
  useContact,
  useMobile,
} from "@with-nx/hooks-n-helpers";
import { useServerRegion } from "@with-nx/region";
import {
  DesignedButton,
  DesignedIcon,
  ExternalLinkButton,
  ReusableCustomerServiceContainer,
  ReusableSection,
  Rule,
} from "@with-nx/simple-ui/atoms";
import {
  CatalogLayout,
  HelpDetails,
  HelpFormModal,
  ReusableBlogList,
  ReusableCMSItemAccordionList,
  ReusableDoubleGradientCard,
  ReusableDownloadCallToAction,
  ReusableGradientHero,
  ReusableWorkshops,
} from "@with-nx/simple-ui/organisms";
import { useState } from "react";
import Toast from "react-hot-toast";
import { FiPhone } from "react-icons/fi";
import { Box } from "simple-effing-primitive-layout";

const HelpCards = ({ mobile, _help }) => {
  const region = useServerRegion();

  return (
    <div className="container">
      <Box
        parse={`d:flex mb:12 ${mobile && "fd:column"}`}
        style={{
          columnGap: "12px",
          rowGap: "12px",
        }}
      >
        <ReusableDoubleGradientCard
          properties={{ height: 180 }}
          bottomless={true}
          key={1}
          gradients={["#9231B4", "#9231B4"]}
          holder={false}
          bright={true}
        >
          <Box css="d:flex fd:column h:100% j:space-between">
            <Box>
              <Rule parse="!ll d:block mb:5 c:?font1">
                Looking For Your Order?
              </Rule>
              <Rule parse="!ps d:block mb:24 c:?font3">
                You can check your order status on your account.
              </Rule>
            </Box>

            <DesignedButton
              size="large"
              label="Check Order Status"
              theme="tetriary"
              href="/profile/order-history"
            />
          </Box>
        </ReusableDoubleGradientCard>

        <ReusableDoubleGradientCard
          properties={{ height: 180 }}
          bottomless={true}
          key={1}
          gradients={["#1C49E9", "#1C49E9"]}
          holder={false}
          bright={true}
        >
          <Box css="d:flex fd:column h:100% j:space-between">
            <Box>
              <Rule parse="!ll d:block mb:5 c:?font1">
                Want to place an order?
              </Rule>
              <Rule parse="!ps d:block mb:24 c:?font3">
                We process within 2 business days
              </Rule>
            </Box>

            {region.id === 1 ? (
              <DesignedButton
                size="large"
                label="Order Form"
                theme="tetriary"
                href={"/legal/documents/order-form"}
              />
            ) : (
              <DesignedButton
                size="large"
                label="Get Started"
                theme="tetriary"
                href={"/contact/start"}
              />
            )}
          </Box>
        </ReusableDoubleGradientCard>
      </Box>

      <Box
        parse={`d:flex mb:12 ${mobile && "fd:column"}`}
        style={{
          columnGap: "12px",
          rowGap: "12px",
        }}
      >
        <ReusableDoubleGradientCard
          properties={{ height: 180 }}
          bottomless={true}
          key={1}
          gradients={["#F44F5A", "#F44F5A"]}
          holder={false}
          bright={true}
          press={() => _help(true)}
        >
          <Box css="d:flex fd:column h:100% j:space-between">
            <Box>
              <Rule parse="!ll d:block mb:5 c:?font1">
                Want to report and issue?
              </Rule>
              <Rule parse="!ps d:block mb:24 c:?font3">
                You can submit a help ticket to our team.
              </Rule>
            </Box>

            <DesignedButton
              size="large"
              label="Submit a Help Ticket"
              theme="tetriary"
              icon={<DesignedIcon name="_ticket" color="var(--primary)" />}
              press={() => _help(true)}
            />
          </Box>
        </ReusableDoubleGradientCard>

        <ReusableDoubleGradientCard
          properties={{ height: 180 }}
          bottomless={true}
          key={1}
          gradients={["#2EB57D", "#2EB57D"]}
          holder={false}
          bright={true}
        >
          <Box css="d:flex fd:column h:100% j:space-between">
            <Box>
              <Rule parse="!ll d:block mb:5 c:?font1">
                Want to submit a feedback?
              </Rule>
              <Rule parse="!ps d:block mb:24 c:?font3">
                Your feedback is very valuable to us.
              </Rule>
            </Box>

            <DesignedButton
              size="large"
              label="Submit Feedback"
              theme="tetriary"
              href="https://form.jotform.com/240914292958062"
            />
          </Box>
        </ReusableDoubleGradientCard>
      </Box>
    </div>
  );
};
export const getServerSideProps = async ({ resolvedUrl }) => {
  return {
    props: {
      resolvedUrl,
    },
  };
};
export default function Page({ resolvedUrl }) {
  const mobile = useMobile();

  const { blogs } = useCMSBlogs({
    filter: {
      slug: [
        "harry-potter-cursed-child-projections",
        "beetlejuice-jr-projections-spotlight",
      ],
    },
    per: 2,
  });

  const { releases } = useCMSRelease();
  const { faq } = useCMSFAQ();
  const contact = useContact();

  const [help, _help] = useState(false);
  const [helpDetails, setHelpDetails] = useState<HelpDetails | undefined>(
    undefined
  );

  const handleSubmitHelp = async () => {
    _help(false);

    const meta = [];

    if (helpDetails.show) {
      meta.push(["Show", helpDetails.show]);
    }

    if (helpDetails.performance_date) {
      meta.push(["Performance Date", helpDetails.performance_date]);
    }

    if (helpDetails.broadway_media_order_number) {
      meta.push(["Order Number", helpDetails.broadway_media_order_number]);
    }

    try {
      await contact.send("help", {
        email: helpDetails?.email,
        organizationName: helpDetails.name,
        firstName: helpDetails?.name?.split(" ")?.[0],
        lastName:
          helpDetails?.name?.split(" ")?.[1] ||
          helpDetails?.name?.split(" ")?.[0] ||
          "",
        message: helpDetails?.message,
        phone: helpDetails?.phone_number,
        subject: "Help Form Submission",
        meta,
      });
      Toast.success("Your message has been sent. We will be in touch soon.");
      setHelpDetails(undefined);
    } catch (error) {
      Toast.error("Something went wrong. Please try again later.");
    }
  };

  return (
    <CatalogLayout
      title="Broadway Media - Support"
      transparentFixedNavBar
      resolvedUrl={resolvedUrl}
    >
      <ReusableGradientHero
        slanted
        backdrop="/assets/hero/contact-us-gradient-webp.webp"
        src="/assets/hero/support-hero.webp"
      >
        <Rule parse="!ds d:block mb:20">Support</Rule>

        <Rule parse="!pm d:block mb:36" color="var(--font2)">
          We pride ourselves on friendly, pro-active support!
        </Rule>

        <Box
          parse="d:flex"
          style={{
            columnGap: "8px",
          }}
        >
          <DesignedButton
            icon={
              <FiPhone size={20} color="var(--background)" title="phone icon" />
            }
            label="Contact Us"
            theme="white"
            href="/contact"
          />

          <DesignedButton
            label="Help Articles"
            theme="tetriary-white"
            href="https://help.broadwaymedia.com"
          />
        </Box>
      </ReusableGradientHero>

      <ReusableSection>
        <ReusableCustomerServiceContainer />
      </ReusableSection>

      <ReusableSection>
        <HelpCards mobile={mobile} _help={_help} />
      </ReusableSection>

      {!!faq?.length && (
        <ReusableSection>
          <ReusableCMSItemAccordionList
            title="Frequently Asked Questions"
            items={faq}
          />
        </ReusableSection>
      )}

      {blogs && (
        <ReusableBlogList
          title="Product Updates & News"
          blogs={blogs}
          seeMore={false}
        />
      )}

      {!mobile && (
        <ReusableSection bottom={0} top={148}>
          <ReusableDownloadCallToAction />
        </ReusableSection>
      )}

      {!!releases?.length && (
        <ReusableSection>
          <ReusableCMSItemAccordionList
            title="Release Notes"
            extraHeader={
              <>
                <ExternalLinkButton
                  label="Support Policy"
                  href="/legal/support-policy"
                />
              </>
            }
            items={releases}
          />
        </ReusableSection>
      )}

      <ReusableSection>
        <ReusableWorkshops />
      </ReusableSection>

      <HelpFormModal
        open={help}
        close={() => _help(false)}
        submit={handleSubmitHelp}
        change={(helpDetails) => setHelpDetails(helpDetails)}
      />
    </CatalogLayout>
  );
}
